import type { ChannelType } from 'src/api/generated';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { formatPhoneNumber } from 'src/modules/shared/utils/formatting/phone.util';
import { motion } from 'framer-motion';
import {
  PiCheckBold,
  PiCopyLight,
  PiDotsThreeVertical,
  PiPencilSimpleLineLight,
  PiPhone,
  PiTrashLight,
} from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Dropdown, {
  type DropdownItem,
} from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import {
  stackedFieldButtonClassName,
  stackedFieldValueClassName,
} from 'src/modules/shared/styles/stacked-field.styles';
import { getInlineIconSize } from 'src/modules/shared/components/atoms/buttons/button/button.styles';
import type { ComponentSize } from 'src/modules/shared/types/component.type';
import {
  getAddressVariant,
  getVariantToneClassName,
  isPhoneChannel,
  isValidAddressForChannel,
} from 'src/modules/communications/utils/contact-address-field.util';

interface EditableAddressFieldDisplayProps {
  actionDisabled: boolean;
  actionLoading: boolean;
  actionTooltip?: string;
  address: string;
  channelType: ChannelType;
  className?: string;
  containerClassName: string;
  loading: boolean;
  onAction: () => void;
  onCopy: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  readOnlyTooltip?: string;
  size: ComponentSize;
  showUtilityMenu: boolean;
  utilityIsCopied: boolean;
}

export const EditableAddressFieldDisplay = ({
  actionDisabled,
  actionLoading,
  actionTooltip,
  address,
  channelType,
  className = '',
  containerClassName,
  loading,
  onAction,
  onCopy,
  onDelete,
  onEdit,
  readOnlyTooltip,
  size,
  showUtilityMenu,
  utilityIsCopied,
}: EditableAddressFieldDisplayProps) => {
  const { t } = useI18n();
  const isPhoneAddress = isPhoneChannel(channelType);
  const isAddressValid = isValidAddressForChannel(channelType, address);
  const displayAddress =
    isPhoneAddress && isAddressValid ? formatPhoneNumber(address) : address;
  const nonEditVariant = getAddressVariant(channelType);
  const nonEditToneClassName = getVariantToneClassName({
    variant: nonEditVariant,
    className,
  });
  const isReadOnly = Boolean(readOnlyTooltip);
  const primaryIconSize = getInlineIconSize(size);
  const secondaryIconSize = getInlineIconSize(size);

  const utilityItems: DropdownItem[] = [];

  if (address) {
    utilityItems.push({
      content: utilityIsCopied
        ? t('shared.inline.copied')
        : t('shared.inline.copy'),
      icon: utilityIsCopied ? (
        <PiCheckBold className={`${secondaryIconSize} text-success-500`} />
      ) : (
        <PiCopyLight className={secondaryIconSize} />
      ),
      onClick: onCopy,
    });
  }

  if (onDelete) {
    utilityItems.push({
      content: (
        <span className="text-danger-600">{t('shared.actions.delete')}</span>
      ),
      icon: <PiTrashLight className={`${secondaryIconSize} text-danger-500`} />,
      onClick: onDelete,
    });
  }

  const editTooltip = readOnlyTooltip ?? t('shared.inline.pressToEdit');

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`flex-1 flex w-full items-center gap-2 ${containerClassName}`}
    >
      <div className="min-w-0 flex-1">
        <Tooltip size="sm" content={editTooltip} contentClassName="w-full">
          <Button
            variant={nonEditVariant}
            appearance="soft"
            size={size}
            onClick={onEdit}
            disabled={isReadOnly || onEdit == null}
            className={`min-w-0 ${stackedFieldButtonClassName} !gap-1.5 ${nonEditToneClassName}`}
          >
            <span
              className={`block min-w-0 flex-1 truncate text-left ${stackedFieldValueClassName.replace(/!/g, '')} ${
                address ? nonEditToneClassName : 'text-secondary-500'
              }`}
            >
              {address ? displayAddress : '—'}
            </span>
            {!isReadOnly && (
              <PiPencilSimpleLineLight
                className={`h-4 w-4 flex-shrink-0 ${nonEditToneClassName}`}
              />
            )}
          </Button>
        </Tooltip>
      </div>

      {isPhoneAddress && (
        <Tooltip size="sm" content={actionTooltip}>
          <Button
            variant={nonEditVariant}
            appearance="soft"
            size={size}
            onClick={onAction}
            isIconOnly
            disabled={actionDisabled}
            loading={actionLoading}
            className={nonEditToneClassName}
          >
            <PiPhone className={primaryIconSize} />
          </Button>
        </Tooltip>
      )}

      {showUtilityMenu && utilityItems.length > 0 && (
        <Dropdown
          items={utilityItems}
          placement="bottom-end"
          mobileTitle={address}
          handler={
            <Button
              variant="neutral"
              appearance="ghost"
              size={size}
              isIconOnly
              disabled={loading}
            >
              <PiDotsThreeVertical className={primaryIconSize} />
            </Button>
          }
        />
      )}
    </motion.div>
  );
};
